import { useCallback, useMemo, useRef } from 'react';
import useDefaultApp from './useDefaultApp';
import useProjectServices, { loadProjectServices, refreshProjectServices } from './useProjectServices';
import useRunnables from './useRunnables';
import { restartService, startService } from '../services/SandboxService';
import { startServices } from '../components/sandbox/startProgress';
import { pinnedItems } from '../utils/runnablesModel';
import {
  pinnedPreviewEntries, resolveRunnablePreview, runnablePreviewTab, runnableStartItem, stackCarrying, withPreviewTab,
} from '../utils/runnablePreview';

/**
 * The Preview tab's pinned runnables (utils/runnablePreview.js) for Canvas: `{ entries, resolve, show, carrierFor,
 * carrierNow, start, retry }`.
 * - `entries`: the pinned files of the project's services, for the header's service picker ("app · docker-compose.yaml");
 * - `resolve(name)`: the entry a requested preview name means (Apps' "Open preview" sends the runner name), or null;
 * - `show(entry, { route })`: its preview tab (the session's previewUrl), added or updated, and made the active one;
 * - `carrierFor(owner)` / `carrierNow(owner)`: the running stack the Preview of `owner` defaults to (now, or from a
 *   fresh runner answer), or null;
 * - `start(tab)` (the stopped state's Start, as Apps' Start) and `retry(tab)` (Retry: a new render with the runnable
 *   payload, `runnable: { kind, path, entry? }` under the owner's serviceName), never the owner's build.
 * @param {{ projectId: (string|null), owners: string[], setPreviewTabs: Function, setActivePreviewTab: Function }} input
 */
export default function useRunnablePreview({ projectId, owners = [], setPreviewTabs, setActivePreviewTab }) {
  const runnables = useRunnables(projectId);
  const { services: runnerServices } = useProjectServices(projectId, { intervalMs: 10000, enabled: Boolean(projectId) });
  const { defaultRunnable } = useDefaultApp(projectId, { enabled: Boolean(projectId) });
  const ownersKey = (owners || []).join('|');
  const entries = useMemo(
    () => pinnedPreviewEntries({ pinned: pinnedItems(runnables.scan, runnables.choices), owners, runnerServices, defaultRunnable }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runnables.scan, runnables.choices, ownersKey, runnerServices, defaultRunnable],
  );
  const latest = useRef(null);
  latest.current = { entries, runnerServices, defaultRunnable };

  const patch = useCallback((serviceName, change) => {
    setPreviewTabs((tabs) => tabs.map((tab) => (tab.serviceName === serviceName ? { ...tab, ...change } : tab)));
  }, [setPreviewTabs]);

  const resolve = useCallback((name) => resolveRunnablePreview(name, latest.current.entries, latest.current.runnerServices), []);

  const show = useCallback((entry, { route = '/' } = {}) => {
    if (!projectId || !entry) { return; }
    const tab = runnablePreviewTab(projectId, entry, { route });
    setPreviewTabs((current) => {
      const before = (current || []).find((item) => item.serviceName === tab.serviceName);
      // Already showing its app at the same place: made active, not reloaded.
      const keep = before?.url && tab.url && before.url === tab.url;
      const { tabs, index } = withPreviewTab(current, keep ? { ...tab, iframeKey: before.iframeKey, iframeLoaded: before.iframeLoaded } : tab);
      setActivePreviewTab(index);
      return tabs;
    });
  }, [projectId, setPreviewTabs, setActivePreviewTab]);

  const carrierFor = useCallback((owner) => stackCarrying(projectId, owner, latest.current), [projectId]);

  const carrierNow = useCallback(async(owner) => {
    const answer = await loadProjectServices(projectId).catch(() => null);
    if (!answer?.services) { return stackCarrying(projectId, owner, latest.current); }
    return stackCarrying(projectId, owner, { ...latest.current, runnerServices: answer.services });
  }, [projectId]);

  const start = useCallback(async(tab) => {
    if (!projectId || !tab) { return; }
    patch(tab.serviceName, { stopped: false, starting: true, startRequestedAt: Date.now(), startError: null });
    try {
      const item = runnableStartItem(tab, latest.current.runnerServices);
      if (item) {
        const progress = await startServices(projectId, [item], { onProgress: () => refreshProjectServices(projectId) });
        const failed = progress?.error || progress?.services?.find((entry) => entry.phase === 'failed' || entry.phase === 'limit')?.reason;
        if (failed) { throw new Error(failed); }
      } else {
        await restartService(tab.sessionId);
      }
      refreshProjectServices(projectId);
    } catch (error) {
      patch(tab.serviceName, { stopped: true, starting: false, startRequestedAt: null, startError: error?.message || 'The stack could not be started.' });
    }
  }, [projectId, patch]);

  const retry = useCallback(async(tab) => {
    if (!projectId || !tab) { return; }
    patch(tab.serviceName, {
      error: null, loading: true, followingBuild: true, renderPhase: 'starting', stopped: false, starting: false, iframeKey: Date.now(),
    });
    try {
      if (tab.runnable?.file) {
        await startService(projectId, tab.runnable.service, { runnable: tab.runnable });
      } else {
        await restartService(tab.sessionId);
      }
      refreshProjectServices(projectId);
    } catch (error) {
      patch(tab.serviceName, { loading: false, followingBuild: false, renderPhase: undefined, error: error?.message || 'The stack could not be started.' });
    }
  }, [projectId, patch]);

  return { entries, resolve, show, carrierFor, carrierNow, start, retry };
}
